import { Clock, CheckCircle2, Layers, Rocket } from 'lucide-react';
import type { TrailStep } from './master.types';
import { EmptyStateCard } from './EmptyStateCard';

interface TrailConfigCardProps {
  totalEscolas: number;
  setupAguardando: number;
  pctAguardando: number;
  trailSteps: TrailStep[];
}

const STEP_COLORS = ['#6366f1', '#3b82f6', '#8b5cf6', '#14b8a6', '#f59e0b', '#10b981', '#ef4444'];

export function TrailConfigCard({ totalEscolas, setupAguardando, pctAguardando, trailSteps }: TrailConfigCardProps) {
  return (
    <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 14 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h3 style={{ fontSize: '.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
          <Layers size={16} style={{ color: '#6366f1' }} />
          Trilha de Configuração Global
        </h3>
        <span style={{ fontSize: '.72rem', color: '#94a3b8', fontWeight: 600 }}>
          {totalEscolas} escola{totalEscolas !== 1 ? 's' : ''} monitoradas
        </span>
      </div>

      {totalEscolas === 0 ? (
        <EmptyStateCard
          icon={Rocket}
          title="Pronto para começar!"
          description="Cadastre escolas e acompanhe o progresso da configuração em tempo real aqui."
          compact
        />
      ) : (
        <>

      {/* Aguardando */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #94a3b8' }}>
              <Clock size={11} style={{ color: '#94a3b8' }} />
            </div>
            <span style={{ fontSize: '.82rem', fontWeight: 700, color: '#64748b' }}>Aguardando início</span>
          </div>
          <span style={{ fontSize: '.82rem', fontWeight: 800, color: '#94a3b8', background: '#f1f5f9', padding: '2px 8px', borderRadius: 6 }}>
            {setupAguardando} escolas
          </span>
        </div>
        <div style={{ height: 6, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pctAguardando}%`, background: '#94a3b8', borderRadius: 99, transition: 'width .6s ease' }} />
        </div>
      </div>

      {/* Divisor */}
      <div style={{ borderTop: '1px dashed #e2e8f0', marginBottom: 10 }} />

      {/* Etapas */}
      {trailSteps.map((step, idx) => {
        const pct = totalEscolas > 0 ? Math.round((step.count / totalEscolas) * 100) : 0;
        const color = STEP_COLORS[idx % STEP_COLORS.length];
        const hasItens = step.totalItens !== undefined && step.totalItens > 0;
        const itensLabel = hasItens
          ? `${step.totalItens} ${step.unidade || 'cadastrados'}`
          : (step.count > 0 ? (step.label === 'Grade' ? 'Grade ativa' : 'Concluído') : 'Pendente');

        return (
          <div key={step.label} style={{ marginBottom: 9 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: step.count > 0 ? color : '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `2px solid ${step.count > 0 ? color : '#e2e8f0'}`,
                  flexShrink: 0,
                }}>
                  {step.count > 0
                    ? <CheckCircle2 size={11} style={{ color: 'white' }} />
                    : <span style={{ fontSize: '.6rem', fontWeight: 800, color: '#94a3b8' }}>{idx + 1}</span>
                  }
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: '.82rem',
                    fontWeight: 600,
                    color: step.count > 0 ? 'var(--text-main, #1e293b)' : '#94a3b8',
                  }}>
                    {step.label}
                  </span>
                  {step.totalItens !== undefined && (
                    <span style={{
                      fontSize: '.68rem',
                      fontWeight: 600,
                      color: hasItens ? '#334155' : '#94a3b8',
                      background: hasItens ? '#f8fafc' : '#f1f5f9',
                      border: '1px solid #e2e8f0',
                      padding: '1px 6px',
                      borderRadius: 4,
                      lineHeight: '1.2',
                    }}>
                      {itensLabel}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{
                  fontSize: '.72rem',
                  fontWeight: 600,
                  color: '#64748b',
                  background: '#f8fafc',
                  padding: '1px 6px',
                  borderRadius: 4,
                  border: '1px solid #f1f5f9',
                }}>
                  {step.count}/{totalEscolas} {totalEscolas === 1 ? 'escola' : 'esc.'}
                </span>
                <span style={{
                  fontSize: '.75rem',
                  fontWeight: 700,
                  color,
                  background: `${color}15`,
                  padding: '2px 7px',
                  borderRadius: 6,
                  minWidth: 42,
                  textAlign: 'center',
                }}>
                  {pct}%
                </span>
              </div>
            </div>
            <div style={{ height: 5, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99, transition: 'width .6s ease' }} />
            </div>
          </div>
        );
      })}
        </>
      )}
    </div>
  );
}
